import { create } from 'zustand';
import type { TranscriptEntry, ConversationStatus } from '@mia/shared';
import { api } from '../lib/api';

interface StartSessionResponse {
  conversationId: string;
  roomName: string;
  token: string;
  contact: {
    id: string;
    name: string | null;
    phone: string | null;
    email: string | null;
  } | null;
  isNewContact: boolean;
}

interface SessionState {
  conversationId: string | null;
  roomName: string | null;
  token: string | null;
  status: ConversationStatus;
  transcript: TranscriptEntry[];
  contact: {
    id: string;
    name: string | null;
    phone: string | null;
    email: string | null;
  } | null;
  isNewContact: boolean;
  error: string | null;
  isLoading: boolean;

  startSession: (params: {
    channel: 'web' | 'phone';
    contactPhone?: string;
    contactEmail?: string;
    businessId?: string;
    agentId?: string;
  }) => Promise<void>;
  endSession: (params?: {
    outcome?: string;
    summary?: string;
    keyPoints?: string[];
  }) => Promise<void>;
  addTranscriptEntry: (entry: TranscriptEntry) => void;
  updateStatus: (status: ConversationStatus) => void;
  reset: () => void;
}

const initialState = {
  conversationId: null,
  roomName: null,
  token: null,
  status: 'pending' as ConversationStatus,
  transcript: [],
  contact: null,
  isNewContact: false,
  error: null,
  isLoading: false,
};

export const useSessionStore = create<SessionState>((set, get) => ({
  ...initialState,

  startSession: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<StartSessionResponse>('/sessions/start', params);
      const data = response.data;
      
      if (data) {
        set({
          conversationId: data.conversationId,
          roomName: data.roomName,
          token: data.token,
          contact: data.contact,
          isNewContact: data.isNewContact,
          status: 'connecting',
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to start session',
        isLoading: false,
      });
      throw error;
    }
  },

  endSession: async (params) => {
    const { conversationId } = get();
    if (!conversationId) return;

    set({ isLoading: true });
    try {
      await api.post(`/sessions/${conversationId}/end`, params || {});
      set({ status: 'completed', isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to end session',
        isLoading: false,
      });
    }
  },

  addTranscriptEntry: (entry) => {
    set((state) => ({
      transcript: [...state.transcript, entry],
    }));
  },

  updateStatus: (status) => {
    set({ status });
  },

  reset: () => {
    set(initialState);
  },
}));

